import { Component, OnDestroy, OnInit } from '@angular/core';
import { RouterModule, RouterOutlet } from '@angular/router';

import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { PrimeNG } from 'primeng/config';

import { ClientQuest } from 'src/theme';

import { JwtService } from '@core/services/jwt.service';
import { NotificationService } from '@core/services/notification.service';
import { UpdateService } from '@core/services/update.service';
import { FollowUpService } from '@shared/services/followup.service';
import { ButtonModule } from 'primeng/button';
import { CommonModule } from '@angular/common';
import { Store } from '@ngxs/store';
import { AuthState } from '@core/states/auth.state';
import { Subject, takeUntil } from 'rxjs';
import { ActivitiesCategory } from '@shared/enums/activities.enum';
import { ActivitiesCategoryService } from '@shared/services/activities-category.service';
import { SalutationPipe } from '@shared/pipes/salutation.pipe';

/**
 * @title AppComponent
 * @since 1.0.0
 * 
 * @description The root component of the application.
 * Handles global initialization tasks such as JWT validation, notification permissions, and update checks.
 * It also manages the display of update notifications and confirmation dialogs.
 * 
 */
@Component({
	selector: 'app-root',
	standalone: true,
	imports: [
		CommonModule,
		RouterOutlet,
		ToastModule,
		ConfirmDialogModule,
		ButtonModule,
		RouterModule,
		SalutationPipe
	],
	templateUrl: './app.component.html',
	styleUrl: './app.component.scss',
	providers: [ConfirmationService]
})
export class AppComponent implements OnInit, OnDestroy {

	private destroy$ = new Subject<void>();

	/**
	 * @param {JwtService} jwtService - Service for validating JWT tokens.
	 * @param {NotificationService} notificationService - Service for managing browser notifications.
	 * @param {UpdateService} updateService - Service for handling application updates.
	 * @param {ConfirmationService} confirmationService - Service for displaying confirmation dialogs.
	 * @param {PrimeNG} primeng - PrimeNG configuration service for theming.
	 * @since 1.0.0
	 */
	constructor(
		private jwtService: JwtService,
		private notificationService: NotificationService,
		private updateService: UpdateService,
		private followUpService: FollowUpService,
		private confirmationService: ConfirmationService,
		private categoryService: ActivitiesCategoryService,
		private store: Store,
		private primeng: PrimeNG
	) {
		// Initialize the theme for PrimeNG components
		this.primeng.theme.set({
			preset: ClientQuest,
			options: {
				cssLayer: {
					name: 'primeng',
					order: 'tailwind-base, primeng, tailwind-utilities'
				},
				darkModeSelector: '.theme-dark'
			}
		});
	}

	ngOnInit() {

		// If the JWT is invalid or expired, perform logout
		this.jwtService.validate();

		// Check for updates
		this.updateService.updateAvailable.subscribe(() => {
			this.openUpdateDialog();
			this.sendNotification('Client Quest Update verfügbar', {
				body: 'Bitte laden Sie die Seite neu, um die neueste Version zu erhalten.',
				icon: '/assetes/images/favicon-96x96.png'
			});
		});

		// Check if the user is authenticated
		this.store.select(AuthState.isAuthenticated)
			.pipe(
				takeUntil(this.destroy$)
			)
			.subscribe((isAuthenticated) => {
				if (isAuthenticated) {
					// Request permission for browser notifications
					this.notificationService.requestPermission();
					// Start polling for follow-up notifications
					this.followUpService.startPolling();				
				}
			});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	/**
	 * Displays a notification to the user.
	 * @param {string} header - The header of the notification.
	 * @param {object} payload - The payload containing the body and icon of the notification.
	 * @since 1.0.0
	 * @returns {void}
	 */
	sendNotification(header: string, payload: { body: string; icon: string }): void {
		this.notificationService.showNotification(header, payload);
	}

	/**
	 * Opens a confirmation dialog to prompt the user to reload the page for an update.
	 * @since 1.0.0
	 * @returns {void}
	 * @todo Localize the dialog messages for internationalization (i18n).
	 */
	openUpdateDialog(): void {
		this.confirmationService.confirm({
			message: 'Es ist eine neue Version verfügbar. Aktualisieren Sie die Seite, um die neueste Version zu laden. Alle ungespeicherten Änderungen gehen verloren.</b>',
			header: 'Update verfügbar',
			icon: 'pi pi-exclamation-circle',
			rejectButtonStyleClass: 'p-button-text',
			rejectButtonProps: {
				label: 'Abbrechen',
				severity: 'secondary',
				text: true,
				autoFocus: false
			},
			acceptButtonProps: {
				label: 'Neu laden',
				text: true
			},
			accept: () => {
				this.updateService.activateUpdate();
			},
			key: 'updateDialog'
		});
	}

	/**
	 * Returns the icon class based on the activity category.
	 * @param {ActivitiesCategory} category - The category of the activity.
	 * @returns {string} The icon class for the activity category.
	 * @since 1.0.0
	 */
	getActivityStyle(category: ActivitiesCategory) {
		return this.categoryService.getCategoryStyle(category);
	}

}
